import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { setSessionSecret, clearSessionSecret } from '../api/client';

// Initial state
const initialState = {
  // Initialization tracking
  isInitialized: false, // True once session restoration from localStorage is complete
  
  // Step 1: Yearbook type
  yearbookType: null, // 'group' | 'couple' | 'family'
  
  // Step 2: Upload
  sessionId: null,
  sessionSecret: null, // Auth token for API requests - keep private!
  uploadedFile: null,
  
  // Step 3: Analysis results
  stats: null, // { participants, totalMessages, dateRange, mediaCount, years }
  
  // Step 4: Date range (currently unused in UI; all years are included by default)
  customDateRange: null, // { start: string, end: string } or null for all years
  
  // Step 5: Privacy & style
  nameStyle: 'full', // 'full' | 'initials' | 'anonymous'
  hidePhoneNumbers: true,
  tone: 'clean', // 'clean' | 'funny' | 'emotional'
  previewStoryCount: 3, // Number of stories to generate in preview (0 = all)
  
  // Step 6: Book setup (cover & title)
  bookTitle: '',
  bookSubtitle: '',
  bookDedication: '',
  coverImageUrl: null, // URL to uploaded cover image
  
  // Build status
  buildStatus: null, // { status, progress, message, resultUrl }
  
  // UI state
  isLoading: false,
  error: null,
};

// Session storage keys
const STORAGE_KEYS = {
  SESSION_ID: 'yearbook_session_id',
  SESSION_SECRET: 'yearbook_session_secret',
};

// Action types
const ActionTypes = {
  SET_INITIALIZED: 'SET_INITIALIZED',
  SET_YEARBOOK_TYPE: 'SET_YEARBOOK_TYPE',
  SET_SESSION: 'SET_SESSION',
  SET_STATS: 'SET_STATS',
  SET_CUSTOM_DATE_RANGE: 'SET_CUSTOM_DATE_RANGE',
  SET_PRIVACY_SETTINGS: 'SET_PRIVACY_SETTINGS',
  SET_PREVIEW_STORY_COUNT: 'SET_PREVIEW_STORY_COUNT',
  SET_BOOK_SETUP: 'SET_BOOK_SETUP',
  SET_COVER_IMAGE: 'SET_COVER_IMAGE',
  SET_BUILD_STATUS: 'SET_BUILD_STATUS',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  RESET: 'RESET',
};

// Reducer
function yearbookReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_INITIALIZED:
      return { ...state, isInitialized: true };
    
    case ActionTypes.SET_YEARBOOK_TYPE:
      return { ...state, yearbookType: action.payload };
    
    case ActionTypes.SET_SESSION:
      return {
        ...state,
        sessionId: action.payload.sessionId,
        sessionSecret: action.payload.sessionSecret,
        uploadedFile: action.payload.filename,
      };
    
    case ActionTypes.SET_STATS:
      return { ...state, stats: action.payload };
    
    case ActionTypes.SET_CUSTOM_DATE_RANGE:
      return { ...state, customDateRange: action.payload };
    
    case ActionTypes.SET_PRIVACY_SETTINGS:
      return {
        ...state,
        nameStyle: action.payload.nameStyle ?? state.nameStyle,
        hidePhoneNumbers: action.payload.hidePhoneNumbers ?? state.hidePhoneNumbers,
        tone: action.payload.tone ?? state.tone,
      };
    
    case ActionTypes.SET_PREVIEW_STORY_COUNT:
      return { ...state, previewStoryCount: action.payload };
    
    case ActionTypes.SET_BOOK_SETUP:
      return {
        ...state,
        bookTitle: action.payload.title ?? state.bookTitle,
        bookSubtitle: action.payload.subtitle ?? state.bookSubtitle,
        bookDedication: action.payload.dedication ?? state.bookDedication,
      };
    
    case ActionTypes.SET_COVER_IMAGE:
      return { ...state, coverImageUrl: action.payload };
    
    case ActionTypes.SET_BUILD_STATUS:
      return { ...state, buildStatus: action.payload };
    
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
    
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload };
    
    case ActionTypes.RESET:
      return { ...initialState, isInitialized: true };
    
    default:
      return state;
  }
}

// Context
const YearbookContext = createContext(null);

// Provider component
export function YearbookProvider({ children }) {
  const [state, dispatch] = useReducer(yearbookReducer, initialState);
  
  // Restore session from localStorage on mount
  useEffect(() => {
    const storedSessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    const storedSessionSecret = localStorage.getItem(STORAGE_KEYS.SESSION_SECRET);
    
    if (storedSessionId && storedSessionSecret) {
      // Restore the session to the API client
      setSessionSecret(storedSessionSecret);
      dispatch({
        type: ActionTypes.SET_SESSION,
        payload: {
          sessionId: storedSessionId,
          sessionSecret: storedSessionSecret,
          filename: null, // We don't store the filename
        },
      });
    }
    
    // Mark initialization as complete
    dispatch({ type: ActionTypes.SET_INITIALIZED });
  }, []);
  
  // Action creators
  const setYearbookType = useCallback((type) => {
    dispatch({ type: ActionTypes.SET_YEARBOOK_TYPE, payload: type });
  }, []);
  
  const setSession = useCallback((sessionId, sessionSecret, filename) => {
    // Persist to localStorage
    if (sessionId && sessionSecret) {
      localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
      localStorage.setItem(STORAGE_KEYS.SESSION_SECRET, sessionSecret);
      // Also set in the API client
      setSessionSecret(sessionSecret);
    }
    dispatch({ type: ActionTypes.SET_SESSION, payload: { sessionId, sessionSecret, filename } });
  }, []);
  
  const setStats = useCallback((stats) => {
    dispatch({ type: ActionTypes.SET_STATS, payload: stats });
  }, []);
  
  const setCustomDateRange = useCallback((range) => {
    dispatch({ type: ActionTypes.SET_CUSTOM_DATE_RANGE, payload: range });
  }, []);
  
  const setPrivacySettings = useCallback((settings) => {
    dispatch({ type: ActionTypes.SET_PRIVACY_SETTINGS, payload: settings });
  }, []);
  
  const setPreviewStoryCount = useCallback((count) => {
    dispatch({ type: ActionTypes.SET_PREVIEW_STORY_COUNT, payload: count });
  }, []);
  
  const setBookSetup = useCallback(({ title, subtitle, dedication }) => {
    dispatch({ type: ActionTypes.SET_BOOK_SETUP, payload: { title, subtitle, dedication } });
  }, []);
  
  const setCoverImage = useCallback((url) => {
    dispatch({ type: ActionTypes.SET_COVER_IMAGE, payload: url });
  }, []);
  
  const setBuildStatus = useCallback((status) => {
    dispatch({ type: ActionTypes.SET_BUILD_STATUS, payload: status });
  }, []);
  
  const setLoading = useCallback((isLoading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: isLoading });
  }, []);
  
  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);
  
  const reset = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.SESSION_SECRET);
    // Clear API client
    clearSessionSecret();
    dispatch({ type: ActionTypes.RESET });
  }, []);
  
  // Build config helper
  const getBuildConfig = useCallback(() => {
    return {
      yearbook_type: state.yearbookType,
      custom_start_date: state.customDateRange?.start || null,
      custom_end_date: state.customDateRange?.end || null,
      name_style: state.nameStyle,
      hide_phone_numbers: state.hidePhoneNumbers,
      tone: state.tone,
      preview_story_count: state.previewStoryCount,
    };
  }, [state]);
  
  const value = {
    // State
    ...state,
    
    // Actions
    setYearbookType,
    setSession,
    setStats,
    setCustomDateRange,
    setPrivacySettings,
    setPreviewStoryCount,
    setBookSetup,
    setCoverImage,
    setBuildStatus,
    setLoading,
    setError,
    reset,
    
    // Helpers
    getBuildConfig,
  };
  
  return (
    <YearbookContext.Provider value={value}>
      {children}
    </YearbookContext.Provider>
  );
}

// Custom hook
export function useYearbook() {
  const context = useContext(YearbookContext);
  if (!context) {
    throw new Error('useYearbook must be used within a YearbookProvider');
  }
  return context;
}

export default YearbookContext;

